import React, { useState } from 'react'
import {
  Navbar,
  NavLink,
  NavSectionHeader,
  PageContainer,
  LibraryThemeProvider,
  flockTheme,
  flockSquareTheme,
  QuotationLargeIcon,
  TrackedButton,
  TrackedFooter,
} from '@flock/shared-ui'
import { sessionStore } from '@flock/utils'
import {
  Box,
  Grid,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useCalendlyEventListener } from 'react-calendly'
import {
  ABOUT_US_PATH,
  AGENTS_PATH,
  BLOG_PATH,
  FAQ_PATH,
  HOMEPAGE_PATH,
  HOW_IT_WORKS_PATH,
  INVESTOR_PATH,
  INVEST_WITH_FLOCK_PATH,
  ONBOARDING_PATH,
  PORTFOLIO_PATH,
  RENT_SELL_FLOCK_CALCULATOR_PATH,
  STORIES_PATH,
  WHAT_IS_FLOCK_PATH,
  FLOCK_CAREERS_URL,
} from '@flock/shared-ui/src/routeConstants'
import { track } from '../../utils/analytics'
import SectionLayout from './SectionLayout'
import PageBanner from './PageBanner'
import WayneAndVickyImage from '../../images/wayne-and-vicky.webp'
import NavbarAsset from '../../images/navbar-asset.webp'

const navItems = [
  {
    label: 'For Landlords',
    dropdownContent: (
      <Box display="flex" flexDirection="row">
        <Box padding="32px!important" width="250px">
          <NavLink to={WHAT_IS_FLOCK_PATH}>What is Flock</NavLink>
          <NavLink to={HOW_IT_WORKS_PATH}>How it Works</NavLink>
          <NavLink to={FAQ_PATH}>Frequently Asked Questions</NavLink>
          <NavLink to={RENT_SELL_FLOCK_CALCULATOR_PATH}>
            Sale Calculator
          </NavLink>
          <NavLink to={AGENTS_PATH}>For Agents</NavLink>
        </Box>
        <Box
          padding="32px!important"
          sx={{
            backgroundColor: 'green0.main',
            borderTopRightRadius: '16px',
            borderBottomRightRadius: '16px',
          }}
        >
          <Box display="flex" flexDirection="row" gap="16px">
            <Box
              component="img"
              alt="Wayne and Vicky Image"
              src={WayneAndVickyImage}
              width="121px"
              height="121px"
            />
            <Box display="flex" flexDirection="column" gap="16px">
              <Box position="absolute" sx={{ mt: '-4px', ml: '-8px' }}>
                <QuotationLargeIcon width="47px" height="32px" />
              </Box>
              <Box width="272px">
                <Typography
                  variant="p2"
                  color="moneyGreen.main"
                  sx={{ textWrap: 'wrap' }}
                >
                  We have been landlords and businesspeople for a long time. We
                  wanted to retire from that. We wanted to hand over our
                  properties and still hold value.
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box pt="24px">
            <TrackedButton
              variant="secondary"
              size="small"
              to={STORIES_PATH}
              sx={{ fontSize: '16px', lineHeight: '24px' }}
            >
              Read Flock Stories
            </TrackedButton>
          </Box>
        </Box>
      </Box>
    ),
  },
  {
    label: 'Investment',
    dropdownContent: (
      <Box display="flex" flexDirection="row">
        <Box padding="32px!important" width="150px">
          <NavLink to={PORTFOLIO_PATH}>Portfolio</NavLink>
          <NavLink to={INVEST_WITH_FLOCK_PATH}>Invest with Flock</NavLink>
        </Box>
        <Box
          padding="32px!important"
          sx={{
            backgroundColor: 'green0.main',
            borderTopRightRadius: '16px',
            borderBottomRightRadius: '16px',
          }}
        >
          <Box display="flex" flexDirection="row" gap="16px">
            <Box
              component="img"
              alt="Navigation Asset Image"
              src={NavbarAsset}
              width="392px"
            />
          </Box>
          <Box pt="24px">
            <TrackedButton
              variant="secondary"
              size="small"
              to={PORTFOLIO_PATH}
              sx={{ fontSize: '16px', lineHeight: '24px' }}
            >
              See details on Flock Homes
            </TrackedButton>
          </Box>
        </Box>
      </Box>
    ),
  },
  {
    label: 'About',
    dropdownContent: (
      <Box display="flex" flexDirection="row">
        <Box padding="32px!important" width="85px">
          <NavLink to={ABOUT_US_PATH}>About Us</NavLink>
          <NavLink to={FLOCK_CAREERS_URL}>Careers</NavLink>
        </Box>
        <Box
          padding="32px!important"
          sx={{
            backgroundColor: 'green0.main',
            borderTopRightRadius: '16px',
            borderBottomRightRadius: '16px',
          }}
        >
          <Box display="flex" flexDirection="row" gap="16px">
            <Box
              color="moneyGreen.main"
              sx={{ backgroundColor: 'moneyGreen.main' }}
              width="4px"
              height="72px"
            />
            <Box width="276px">
              <Typography
                variant="p2"
                color="moneyGreen.main"
                sx={{ textWrap: 'wrap' }}
              >
                How We Manage Total Rental Income: A Strategic Approach to
                Maximizing Real Estate Revenue
              </Typography>
            </Box>
          </Box>
          <Box pt="24px">
            <TrackedButton
              variant="secondary"
              size="small"
              to={BLOG_PATH}
              sx={{ fontSize: '16px', lineHeight: '24px' }}
            >
              Read the Flock Blog
            </TrackedButton>
          </Box>
        </Box>
      </Box>
    ),
  },
]

const mobileNavItems = (
  <Grid container spacing={1}>
    <Grid item xs={6}>
      <Box height="312px">
        <NavSectionHeader fontFamily="Outfit">For Landlords</NavSectionHeader>
        <NavLink to={WHAT_IS_FLOCK_PATH}>What is Flock</NavLink>
        <NavLink to={HOW_IT_WORKS_PATH}>How it Works</NavLink>
        <NavLink to={FAQ_PATH}>FAQ</NavLink>
        <NavLink to={RENT_SELL_FLOCK_CALCULATOR_PATH}>Sale Calculator</NavLink>
        <NavLink to={AGENTS_PATH}>For Agents</NavLink>
        <NavLink to={STORIES_PATH}>Client Stories</NavLink>
      </Box>
      <Box>
        <NavSectionHeader fontFamily="Outfit">About</NavSectionHeader>
        <NavLink to={ABOUT_US_PATH}>About Us</NavLink>
        <NavLink to={FLOCK_CAREERS_URL}>Careers</NavLink>
        <NavLink to={BLOG_PATH}>The Flock Blog</NavLink>
      </Box>
    </Grid>
    <Grid item xs={6}>
      <Box height="312px">
        <NavSectionHeader fontFamily="Outfit">Investment</NavSectionHeader>
        <NavLink to={PORTFOLIO_PATH}>Portfolio</NavLink>
        <NavLink to={INVEST_WITH_FLOCK_PATH}>Invest with Flock</NavLink>
      </Box>
      <Box>
        <NavSectionHeader fontFamily="Outfit">Account</NavSectionHeader>
        <NavLink to={INVESTOR_PATH}>Sign In</NavLink>
        <NavLink to={ONBOARDING_PATH}>Get Started</NavLink>
      </Box>
    </Grid>
  </Grid>
)

const actions = [
  {
    label: 'Sign In',
    variant: 'primary',
    onClick: () => {
      window.location.href = INVESTOR_PATH
    },
  },
  {
    label: 'Get Started',
    variant: 'secondary',
    onClick: () => {
      window.location.href = ONBOARDING_PATH
    },
    trackingConfig: {
      properties: {
        leadConversion: 'started',
        pageWrapperCta: true,
      },
    },
  },
]

type PageWrapperProps = {
  title: string
  headerBackground: string
  trackingName?: string

  children: React.ReactNode

  cobrandingConfig?: {
    fullName: string
    websiteUrl: string
    imageUrl: string
  }
  metaTags?: any[]
  asOfDate?: Date
  useSquareTheme?: boolean
  hideNavbar?: boolean
  hideFooterNav?: boolean
  logoPath?: string
  footerPhone?: string
  footerEmail?: string
}

const PageWrapper = (props: PageWrapperProps) => {
  const {
    title,
    headerBackground,
    trackingName,
    children,
    cobrandingConfig,
    metaTags,
    asOfDate,
    useSquareTheme,
    hideNavbar,
    hideFooterNav,
    logoPath,
    footerPhone,
    footerEmail,
  } = props

  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })

  const [bannerOpen, setBannerOpen] = useState(
    sessionStore.getItem('bannerOpen')
      ? sessionStore.getItem('bannerOpen') === 'true'
      : true
  )

  const isSSR = typeof window === 'undefined'

  useCalendlyEventListener({
    onEventScheduled: () => {
      track('scheduledSalesCall')
    },
  })

  return (
    <ThemeProvider theme={useSquareTheme ? flockSquareTheme : flockTheme}>
      <LibraryThemeProvider
        theme={useSquareTheme ? flockSquareTheme : flockTheme}
      >
        <PageContainer
          title={title}
          trackingName={trackingName}
          metaTags={metaTags}
        >
          <Box id="calendlyPopupRoot" height="auto">
            {bannerOpen && !isTablet && !hideNavbar && (
              <PageBanner setBannerOpen={setBannerOpen} />
            )}
            {!hideNavbar ? (
              <Navbar
                logoPath={logoPath || HOMEPAGE_PATH}
                navItems={navItems}
                mobileNavItems={mobileNavItems}
                actions={actions}
                headerBackground={headerBackground}
                cobrandingConfig={cobrandingConfig}
                isHomepage={false}
              />
            ) : null}
            {children}
            <SectionLayout name="footer" backgroundColor="gray1.main">
              <Grid item xs={12}>
                {!isSSR && (
                  <React.Suspense fallback="">
                    <TrackedFooter
                      hideNavItems={hideFooterNav}
                      logoPath={logoPath || HOMEPAGE_PATH}
                      asOfDate={asOfDate}
                      phone={footerPhone}
                      email={footerEmail}
                    />
                  </React.Suspense>
                )}
              </Grid>
            </SectionLayout>
          </Box>
        </PageContainer>
      </LibraryThemeProvider>
    </ThemeProvider>
  )
}

PageWrapper.defaultProps = {
  trackingName: '',
  cobrandingConfig: undefined,
  metaTags: undefined,
  asOfDate: undefined,
  useSquareTheme: false,
  hideNavbar: false,
  hideFooterNav: false,
  logoPath: undefined,
  footerEmail: '',
  footerPhone: '',
}

export default PageWrapper
